'use client';

import { useEffect } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';

import { useAtomValue, useSetAtom } from 'jotai';

import { MessageFormValues } from './project-response.hooks';
import {
  syncFormContentToSelectionAtom,
  syncSelectionAtomToForm,
} from './project-usability-image-choice-form-sync.helpers';
import { imageChoiceSelectionAtom } from './project-usability-image-choice.atoms';

interface Props {
  /** Id of the current Image Choice assistant message. Sync only runs when active. */
  messageId: string | null;
}

/**
 * Bridges the chat-mode sidebar RHF form's `content` field with the shared
 * Image Choice selection atom — so selecting in the main-area stage updates
 * the sidebar (and the Send button's enabled state), and vice versa.
 *
 * Must be rendered inside ProjectChat's FormProvider tree (useFormContext).
 *
 * Each direction reads the opposite side imperatively (getValues /
 * functional setter) so the two effects can't ping-pong through stale
 * closures.
 */
export default function ProjectUsabilityImageChoiceFormSync({
  messageId,
}: Props) {
  const { setValue, control, getValues } = useFormContext<MessageFormValues>();
  const selection = useAtomValue(imageChoiceSelectionAtom);
  const setSelection = useSetAtom(imageChoiceSelectionAtom);
  const content = useWatch({ control, name: 'content' });

  // Atom → form: only triggered by atom selection changes.
  useEffect(
    () =>
      syncSelectionAtomToForm({ selection, messageId, getValues, setValue }),
    [selection, messageId, setValue, getValues]
  );

  // Form → atom: only triggered by form content changes. Uses the functional
  // setter to read the latest atom without taking a dependency on it.
  useEffect(
    () => syncFormContentToSelectionAtom({ content, messageId, setSelection }),
    [content, messageId, setSelection]
  );

  return null;
}
